/**
 * Demo-venue query + mutation layer. The context flag keys under `["venue", venueId, "demo"]`; it is
 * effectively immutable for the session (a venue does not stop being a demo mid-call), so it is cached
 * aggressively. The reset mutation invalidates every `["venue", venueId, …]` query so the whole surface
 * refetches the freshly re-anchored baseline after a destroy-and-reseed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { demoApi, type DemoContext, type DemoResetResponse } from "./demoApi";

export const demoKeys = {
  context: (venueId: string) => ["venue", venueId, "demo", "context"] as const,
};

export function useDemoContext(venueId: string) {
  return useQuery<DemoContext>({
    queryKey: demoKeys.context(venueId),
    queryFn: ({ signal }) => demoApi.context(venueId, signal),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useDemoReset(venueId: string) {
  const qc = useQueryClient();
  return useMutation<DemoResetResponse>({
    mutationFn: () => demoApi.reset(venueId),
    onSettled: () => qc.invalidateQueries({ queryKey: ["venue", venueId] }),
  });
}
